import { toolkitUrls } from '@/util/links'
import { getCookie, getHandleRequest, getCSRFHeaders } from '@/authentication/request_handler'
import type { LibraryImpactOfDisability } from '@/library/library_types'
import type {
  ImpactArea,
  Disability,
  DisabilitiesAndImpactAreasResponse,
  GenerateIODStatementsPayload,
  GeneratedStatement,
  GenerateIODStatementsResponse
} from './impact-of-disability_types'

export async function getImpactOfDisabilityStatement(uuid: string): Promise<LibraryImpactOfDisability> {
  const handleRequest = getHandleRequest()
  if (handleRequest) {
    return handleRequest({ requestFn: getImpactOfDisabilityStatementRequest }, uuid) as Promise<LibraryImpactOfDisability>
  }
  throw new Error('Request handler not initialized')
}

export async function getImpactOfDisabilityStatementRequest(uuid: string): Promise<Response> {
  const url = `${toolkitUrls.apiBases.library}/iod/${uuid}`
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getCSRFHeaders()
  })
  return response
}

export async function getSharedImpactOfDisabilityStatement(uuid: string): Promise<LibraryImpactOfDisability & { user_name?: string }> {
  const url = `${toolkitUrls.apiBases.library}/shared/iod/${uuid}?author=true`
  const response = await fetch(url, {
    method: 'GET'
  })
  
  if (!response.ok) {
    throw new Error(`Failed to fetch shared Impact of Disability statement: ${response.status}`)
  }
  
  try {
    return await response.json() as LibraryImpactOfDisability & { user_name?: string }
  } catch (error) {
    throw new Error(`Failed to parse shared Impact of Disability statement: ${error}`)
  }
}

export async function postImpactOfDisabilityStatement(
  statement: Partial<LibraryImpactOfDisability>
): Promise<LibraryImpactOfDisability> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      return await fetch(`${toolkitUrls.apiBases.library}/iod`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        body: JSON.stringify(statement),
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as LibraryImpactOfDisability
  } catch (e) {
    throw new Error('Error creating Impact of Disability statement', { cause: e })
  }
}

export async function updateImpactOfDisabilityStatement(
  uuid: string, 
  fieldsToUpdate: Partial<LibraryImpactOfDisability>
): Promise<LibraryImpactOfDisability> {
  try {
    const requestFn = async () => {
      const gbcs = getCookie('gbcs')
      return await fetch(`${toolkitUrls.apiBases.library}/iod/${uuid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': gbcs
        },
        body: JSON.stringify(fieldsToUpdate),
        credentials: 'include'
      })
    }
    const requestHandler = getHandleRequest()
    return await requestHandler({ requestFn }) as LibraryImpactOfDisability
  } catch (e) {
    throw new Error('Error updating Impact of Disability statement', { cause: e })
  }
}

async function getDisabilitiesAndImpactAreasRequest(
  licenseSubType: string,
  contentProfile: string
): Promise<Response> {
  const params = new URLSearchParams({
    license_sub_type: licenseSubType,
    content_profile: contentProfile
  })
  
  return await fetch(
    `${import.meta.env.VITE_PRESENTLEVELS_URL}/presentlevels/api/v1/impact-of-disability/disabilities?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    }
  )
}

export async function getDisabilitiesAndImpactAreas(
  licenseSubType: string,
  contentProfile: string
): Promise<DisabilitiesAndImpactAreasResponse> {
  const handleRequest = getHandleRequest()
  return handleRequest(
    { requestFn: getDisabilitiesAndImpactAreasRequest },
    licenseSubType,
    contentProfile
  ) as Promise<DisabilitiesAndImpactAreasResponse>
}

async function generateIODStatementsRequest(
  payload: GenerateIODStatementsPayload
): Promise<Response> {
  const gbcs = getCookie('gbcs')
  return await fetch(
    `${import.meta.env.VITE_PRESENTLEVELS_URL}/api/v2/iod/statements/generate`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': gbcs
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    }
  )
}

export async function generateIODStatements(
  payload: GenerateIODStatementsPayload
): Promise<GenerateIODStatementsResponse> {
  if (!payload.disability || typeof payload.disability !== 'string') {
    throw new Error('disability is required and must be a string')
  }

  if (!payload.subjects || !Array.isArray(payload.subjects) || payload.subjects.length === 0) {
    throw new Error('subjects is required and must be a non-empty array')
  }

  const handleRequest = getHandleRequest()
  return handleRequest(
    { requestFn: generateIODStatementsRequest },
    payload
  ) as Promise<GenerateIODStatementsResponse>
}
